import { useState } from 'react';
import {
  Modal,
  TextArea,
  InlineLoading,
  InlineNotification,
  Dropdown,
} from '@carbon/react';
import { ChartRelationship } from '@carbon/icons-react';
import './StartFromPromptModal.scss';

interface StartFromPromptModalProps {
  open: boolean;
  onClose: () => void;
  onGenerate: (domain: string) => Promise<void>;
}

interface ExamplePrompt {
  id: string;
  label: string;
  text: string;
  isGroupHeader?: boolean;
}

const EXAMPLE_PROMPT_GROUPS: { category: string; items: ExamplePrompt[] }[] = [
  {
    category: 'Material Science',
    items: [
      {
        id: 'battery-research',
        label: 'Battery Material Research',
        text: 'I want an ontology for battery material research, including cathode materials (NMC, LFP, NCA, LCO), anode materials (graphite, silicon, lithium metal), electrolytes (liquid, solid-state, gel), separators, characterization techniques (XRD, SEM, TEM, electrochemical testing), performance metrics (capacity, cycle life, energy density, power density), manufacturing processes, and safety testing protocols.',
      },
      {
        id: 'optical-coating-research',
        label: 'Optical Coating Research',
        text: 'Create an ontology for optical coating research including thin-film materials (oxides, fluorides, metals), coating types (anti-reflective, high-reflective, dichroic, beam-splitter, polarizing), deposition techniques (sputtering, e-beam evaporation, atomic layer deposition, ion-assisted deposition), substrates (glass, fused silica, polymers), optical properties (refractive index, transmittance, reflectance, absorption, laser damage threshold), characterization methods (spectrophotometry, ellipsometry, AFM), multilayer stack design, and durability testing.',
      },
      {
        id: 'adhesive-research',
        label: 'Adhesive Research',
        text: 'Build an ontology for adhesive research covering adhesive families (epoxy, polyurethane, acrylic, silicone, cyanoacrylate, hot-melt), curing mechanisms (thermal, UV, moisture, anaerobic), key components (resins, hardeners, tackifiers, fillers, additives), substrates (metal, plastic, glass, composite, wood), bond performance metrics (lap shear, peel strength, tensile strength, fatigue, creep), surface preparation methods, environmental aging tests, and application processes (dispensing, lamination, bonding lines).',
      },
    ],
  },
  {
    category: 'Cosmetic Science',
    items: [
      {
        id: 'shampoo-formulation',
        label: 'Shampoo Formulation',
        text: 'Create an ontology for shampoo formulation including surfactant systems (anionic such as SLES, amphoteric such as cocamidopropyl betaine, non-ionic), conditioning agents (cationic polymers, silicones, panthenol), thickeners and rheology modifiers, preservatives, fragrance and essential oils, pH adjusters, chelating agents, active ingredients (anti-dandruff, anti-hair-loss, scalp soothing), water phase, performance attributes (foam quality, viscosity, clarity, sensory feel), stability testing, and regulatory compliance.',
      },
      {
        id: 'lipstick-formulation',
        label: 'Lipstick Formulation',
        text: 'Build an ontology for lipstick formulation including wax systems (carnauba, candelilla, beeswax, ozokerite), oils and emollients (castor oil, jojoba, esters), pigments and lakes, pearlescent effect agents, film formers, antioxidants, preservatives, fragrance, active ingredients (moisturizers, SPF, plumping agents), product types (matte, satin, glossy, long-wear, liquid), texture and sensory properties, color matching, stability and bleed testing, and packaging interactions.',
      },
    ],
  },
  {
    category: 'Finance Science',
    items: [
      {
        id: 'forecasting-fpa',
        label: 'Forecasting FP&A',
        text: 'Create an ontology for Financial Planning & Analysis (FP&A) forecasting covering revenue drivers, cost structures (COGS, OPEX, CAPEX), budget cycles (annual budget, quarterly reforecast, rolling forecast), forecasting methods (top-down, bottom-up, driver-based, statistical, time-series), scenario and sensitivity analysis, variance analysis (actual vs budget vs forecast), KPIs (gross margin, EBITDA, working capital, free cash flow), data sources (ERP, CRM, HRIS), business units and cost centers, planning horizons, and stakeholder reporting.',
      },
      {
        id: 'portfolio-risk-analysis',
        label: 'Portfolio Risk Analysis',
        text: 'Build an ontology for portfolio risk analysis including asset classes (equities, fixed income, commodities, FX, derivatives), risk factors (market, credit, liquidity, operational, counterparty), risk measures (Value-at-Risk, Expected Shortfall, volatility, beta, tracking error, drawdown), modeling techniques (historical simulation, Monte Carlo, parametric, factor models), stress testing and scenario analysis, correlation and covariance matrices, hedging instruments, regulatory frameworks (Basel, FRTB, UCITS), benchmarks, and portfolio construction constraints.',
      },
    ],
  },
  {
    category: 'Food Science',
    items: [
      {
        id: 'petfood-formulation',
        label: 'PetFood Formulation',
        text: 'Create an ontology for pet food formulation covering target species and life stages (puppy, adult dog, senior, kitten, adult cat), product formats (dry kibble, wet/canned, semi-moist, treats, raw), protein sources (chicken, beef, fish, plant proteins), carbohydrate and fiber sources, fats and oils, vitamins and minerals premix, palatability enhancers, preservatives, processing methods (extrusion, retort, freeze-drying), nutritional adequacy standards (AAFCO, FEDIAF), digestibility, shelf-life testing, and feeding trials.',
      },
      {
        id: 'nutritional-food-formulation',
        label: 'Nutritional Food Formulation',
        text: 'Build an ontology for human nutritional food formulation including macronutrients (proteins, carbohydrates, fats), micronutrients (vitamins, minerals, trace elements), bioactive ingredients (probiotics, prebiotics, omega-3, polyphenols, fibers), product categories (functional foods, medical nutrition, sports nutrition, infant formula, meal replacements), target populations and dietary needs, ingredient sources (dairy, plant-based, marine), processing technologies (spray drying, extrusion, fermentation), sensory and texture attributes, nutritional labeling, health claims and regulatory frameworks (Codex, EFSA, FDA), and clinical efficacy studies.',
      },
    ],
  },
  {
    category: 'Energy Science',
    items: [
      {
        id: 'datacenter-electrical',
        label: 'Datacenter Electrical Infrastructure',
        text: 'Create an ontology for datacenter electrical infrastructure including power distribution systems, UPS (Uninterruptible Power Supply) units with battery banks and inverters, PDUs (Power Distribution Units), generators, automatic transfer switches, electrical panels, circuit breakers, power monitoring systems, cooling systems (CRAC/CRAH units), redundancy configurations (N+1, 2N), power capacity planning, and maintenance schedules.',
      },
    ],
  },
  {
    category: 'General Science',
    items: [
      {
        id: 'academic-publication',
        label: 'Academic Publication',
        text: 'Create an ontology for academic publications including researchers and authors, institutions and affiliations, research papers and preprints, journals and conferences, peer-review processes, citation networks, research fields and disciplines, funding sources and grants, datasets and reproducibility artifacts, open-access models, bibliometric indicators (h-index, impact factor), and collaboration networks.',
      },
      {
        id: 'patents',
        label: 'Patents',
        text: 'Build an ontology for the patent domain covering patent documents (utility, design, plant), inventors and assignees, patent offices (USPTO, EPO, WIPO, JPO), classification schemes (IPC, CPC), patent lifecycle stages (filing, publication, examination, grant, maintenance, expiration), claim structures (independent, dependent), prior art and citations, patent families and priority claims, freedom-to-operate analysis, infringement and litigation, licensing agreements, and technology landscape mapping.',
      },
    ],
  },
];

// Build a single flat list with non-selectable group-header rows interleaved
// before each category, so a Carbon Dropdown can render visually-grouped items.
const EXAMPLE_PROMPT_ITEMS: ExamplePrompt[] = EXAMPLE_PROMPT_GROUPS.flatMap(
  (group) => [
    {
      id: `__group_${group.category.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`,
      label: group.category,
      text: '',
      isGroupHeader: true,
    },
    ...group.items,
  ],
);

/**
 * Modal for creating an ontology from a domain description prompt
 * Uses AI to generate initial nodes and relationships
 */
export default function StartFromPromptModal({
  open,
  onClose,
  onGenerate,
}: StartFromPromptModalProps) {
  const [domain, setDomain] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    if (!domain.trim()) {
      setError('Please describe your ontology domain');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      await onGenerate(domain.trim());
      // Success - modal will be closed by parent
      setDomain('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate ontology');
      setIsGenerating(false);
    }
  };

  const handleClose = () => {
    if (!isGenerating) {
      setDomain('');
      setError(null);
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onRequestClose={handleClose}
      modalHeading="Start from Prompt"
      modalLabel="AI-Powered Ontology Generation"
      primaryButtonText={isGenerating ? 'Generating...' : 'Generate Ontology'}
      secondaryButtonText="Cancel"
      onRequestSubmit={handleGenerate}
      onSecondarySubmit={handleClose}
      primaryButtonDisabled={isGenerating || !domain.trim()}
      size="md"
      preventCloseOnClickOutside
      className="start-from-prompt-modal"
    >
      <div className="modal-content">
        <div className="modal-description">
          <ChartRelationship size={20} className="ai-icon" />
          <p>
            Describe the domain for your ontology, and AI will generate an initial
            structure with approximately 20 nodes and their relationships to get you started.
          </p>
        </div>

        {error && (
          <InlineNotification
            kind="error"
            title="Error"
            subtitle={error}
            onCloseButtonClick={() => setError(null)}
            lowContrast
          />
        )}

        <Dropdown<ExamplePrompt>
          id="example-prompts"
          titleText="Example Prompts (Optional)"
          label="Choose an example to get started"
          items={EXAMPLE_PROMPT_ITEMS}
          itemToString={(item) =>
            item && !item.isGroupHeader ? item.label : ''
          }
          itemToElement={(item) => {
            if (!item) return <span />;
            if (item.isGroupHeader) {
              return (
                <span className="example-prompts__group-header">
                  {item.label}
                </span>
              );
            }
            return (
              <span className="example-prompts__item">{item.label}</span>
            );
          }}
          onChange={({ selectedItem }) => {
            if (selectedItem && !selectedItem.isGroupHeader) {
              setDomain(selectedItem.text);
            }
          }}
          disabled={isGenerating}
        />

        <TextArea
          id="domain-description"
          data-testid="prompt-domain-input"
          labelText="Domain Description"
          placeholder="Describe your ontology domain in detail..."
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          rows={6}
          disabled={isGenerating}
          helperText="Be specific about the domain, key concepts, and relationships you want to explore"
        />

        {isGenerating && (
          <div className="generating-status">
            <InlineLoading
              description="Generating ontology structure..."
              status="active"
            />
            <p className="generating-text">
              AI is creating nodes and relationships based on your description.
              This may take a moment...
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}

// Made with Bob